import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { dateToMonDDYYYY,dateTimeToMonDDYYYYWithTime,convertTo12HourFormat } from '../utils/dateUtils';

const FEeventRegistrationsManager = ({ userData }) => {
  const [events, setEvents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState('');
  const [form, setForm] = useState({ inpersondesired: false, needstransportation: false });
  const [submitting, setSubmitting] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [removingRegistration, setRemovingRegistration] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [eventSearch, setEventSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        
        // Fetch official events
        const eventsRes = await axios.get('/api/field-experience-events/by-stage/official', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const filteredEvents = (eventsRes.data.events || []).filter(e => e.status !== 'hidden');
        setEvents(filteredEvents.sort((a, b) => new Date(b.date) - new Date(a.date)));

        // Fetch all valid enrollments with account information
        const enrollmentsRes = await axios.get('/api/enrollments/valid-with-accounts', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEnrollments(enrollmentsRes.data.enrollments || []);
      } catch (err) {
        setError('Failed to load data.');
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleEventChange = (eventId) => {
    setSelectedEventId(eventId);
    setSelectedEnrollmentId('');
    
    // Set form defaults based on event type
    if (eventId) {
      const selectedEvent = events.find(e => e.id === eventId);
      if (selectedEvent) {
        const type = selectedEvent.type;
        if (type === 'Virtual') {
          setForm({ inpersondesired: false, needstransportation: false });
        } else if (type === 'In-Person' || type === 'In Person') {
          setForm({ inpersondesired: true, needstransportation: false });
        } else if (type === 'Hybrid') {
          setForm({ inpersondesired: false, needstransportation: false });
        } else {
          setForm({ inpersondesired: false, needstransportation: false });
        }
      }
    } else {
      setForm({ inpersondesired: false, needstransportation: false });
    }
    
    setSuccess('');
    setError('');
    setRegistrations([]);
    setShowRegisterModal(false);
    
    if (eventId) {
      fetchRegistrations(eventId);
    }
  };

  const fetchRegistrations = async (eventId) => {
    setLoadingRegistrations(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/feeventregistrations/by-event/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistrations(res.data.registrations || []);
    } catch (err) {
      console.error('Error fetching registrations:', err);
      setError('Failed to load registrations for this event.');
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const handleEnrollmentChange = (enrollmentId) => {
    setSelectedEnrollmentId(enrollmentId);
    setSuccess('');
    setError('');
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!selectedEventId || !selectedEnrollmentId) {
      setError('Please select both an event and an enrollment.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Check for existing registration
      const checkRes = await axios.get('/api/feeventregistrations/by-enrollment', {
        params: { 
          feevent_id: selectedEventId,
          enrollment_id: selectedEnrollmentId
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (checkRes.data.registrations && checkRes.data.registrations.length > 0) {
        setError('This student is already registered for this event.');
        setSubmitting(false);
        return;
      }

      // Determine event type and force-safe values for flags
      const currentEvent = events.find(e => e.id === selectedEventId);
      const eventType = currentEvent?.type;

      let inpersondesiredValue = form.inpersondesired;
      let needstransportationValue = form.needstransportation;

      if (eventType === 'Virtual') {
        inpersondesiredValue = false;
        needstransportationValue = false;
      } else if (eventType === 'In-Person' || eventType === 'In Person') {
        inpersondesiredValue = true; // Always true for in-person events
      } else if (eventType === 'Hybrid') {
        // If not attending in person, transportation should be false
        if (!inpersondesiredValue) needstransportationValue = false;
      }

      const payload = {
        feevent_id: selectedEventId,
        enrollment: selectedEnrollmentId,
        inpersondesired: inpersondesiredValue,
        needstransportation: needstransportationValue,
      };

      await axios.post('/api/feeventregistrations/register', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Student successfully registered for the event!');
      // Reset form defaults based on event type to avoid stale state
      if (eventType === 'In-Person' || eventType === 'In Person') {
        setForm({ inpersondesired: true, needstransportation: false });
      } else if (eventType === 'Virtual') {
        setForm({ inpersondesired: false, needstransportation: false });
      } else if (eventType === 'Hybrid') {
        setForm({ inpersondesired: false, needstransportation: false });
      } else {
        setForm({ inpersondesired: false, needstransportation: false });
      }
      setSelectedEnrollmentId('');
      setShowRegisterModal(false);
      
      // Refresh registrations
      if (selectedEventId) {
        fetchRegistrations(selectedEventId);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveRegistration = async (registrationId) => {
    if (!window.confirm('Are you sure you want to remove this registration?')) {
      return;
    }
    
    setRemovingRegistration(registrationId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/feeventregistrations/${registrationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Registration removed successfully!');
      // Refresh registrations
      if (selectedEventId) {
        fetchRegistrations(selectedEventId);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove registration.');
    } finally {
      setRemovingRegistration(null);
    }
  };

  const selectedEvent = events.find(e => e.id === selectedEventId);

  const ComputerIcon = ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="13" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );

  const CarIcon = ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 17h14" />
      <path d="M5 17l1.5-5.5A2 2 0 0 1 8.43 10h7.14a2 2 0 0 1 1.93 1.5L19 17" />
      <rect x="3" y="13" width="18" height="6" rx="1.5" />
      <circle cx="7.5" cy="19" r="1.5" />
      <circle cx="16.5" cy="19" r="1.5" />
    </svg>
  );

  const renderInPersonCell = (val) => (
    <span style={styles.inlineIconCell}>
      {!val && <ComputerIcon />}
      <span>{val ? 'Yes' : 'No'}</span>
    </span>
  );

  const renderTransportCell = (val) => (
    <span style={styles.inlineIconCell}>
      {val && <CarIcon />}
      <span>{val ? 'Yes' : 'No'}</span>
    </span>
  );

  const renderAttendedCell = (attended) => {
    if (attended === null || attended === undefined) {
      return <span style={styles.muted}>—</span>;
    }
    return (
      <span style={attended ? styles.tagAttended : styles.tagAbsent}>
        {attended ? 'Present' : 'Absent'}
      </span>
    );
  };

  const filteredEvents = useMemo(() => {
    const q = eventSearch.trim().toLowerCase();
    if (!q) return events;
    return events.filter(ev => {
      const dateStr = ev.date ? dateToMonDDYYYY(ev.date, false) : '';
      const haystack = `${ev.title || ''} ${ev.type || ''} ${ev.location || ''} ${dateStr}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [events, eventSearch]);

  // Registration form logic based on event type
  const renderRegistrationForm = () => {
    if (!selectedEvent) return null;
    const type = selectedEvent.type;
    
    if (type === 'Virtual') {
      return (
        <form onSubmit={handleRegister} style={styles.formBox}>
          <input type="hidden" name="inpersondesired" value="false" />
          <input type="hidden" name="needstransportation" value="false" />
          <div style={styles.formActions}>
            <button type="submit" style={styles.primaryButton} disabled={submitting || !selectedEnrollmentId}>
              {submitting ? 'Registering…' : 'Register'}
            </button>
          </div>
        </form>
      );
    }
    if (type === 'Hybrid') {
      return (
        <form onSubmit={handleRegister} style={styles.formBox}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Plans to attend in person?</label>
            <div style={styles.radioRow}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="inpersondesired"
                  checked={form.inpersondesired === true}
                  onChange={() => handleFormChange('inpersondesired', true)}
                  disabled={submitting}
                />
                Yes
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="inpersondesired"
                  checked={form.inpersondesired === false}
                  onChange={() => handleFormChange('inpersondesired', false)}
                  disabled={submitting}
                />
                No
              </label>
            </div>
          </div>
          {form.inpersondesired && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Needs transportation?</label>
              <div style={styles.radioRow}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="needstransportation"
                    checked={form.needstransportation === true}
                    onChange={() => handleFormChange('needstransportation', true)}
                    disabled={submitting}
                  />
                  Yes
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="needstransportation"
                    checked={form.needstransportation === false}
                    onChange={() => handleFormChange('needstransportation', false)}
                    disabled={submitting}
                  />
                  No
                </label>
              </div>
            </div>
          )}
          <div style={styles.formActions}>
            <button type="submit" style={styles.primaryButton} disabled={submitting || !selectedEnrollmentId}>
              {submitting ? 'Registering…' : 'Register'}
            </button>
          </div>
        </form>
      );
    }
    if (type === 'In-Person' || type === 'In Person') {
      return (
        <form onSubmit={handleRegister} style={styles.formBox}>
          <input type="hidden" name="inpersondesired" value="true" />
          <div style={styles.formGroup}>
            <label style={styles.label}>Needs transportation?</label>
            <div style={styles.radioRow}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="needstransportation"
                  checked={form.needstransportation === true}
                  onChange={() => handleFormChange('needstransportation', true)}
                  disabled={submitting}
                />
                Yes
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="needstransportation"
                  checked={form.needstransportation === false}
                  onChange={() => handleFormChange('needstransportation', false)}
                  disabled={submitting}
                />
                No
              </label>
            </div>
          </div>
          <div style={styles.formActions}>
            <button type="submit" style={styles.primaryButton} disabled={submitting || !selectedEnrollmentId}>
              {submitting ? 'Registering…' : 'Register'}
            </button>
          </div>
        </form>
      );
    }
    return <div style={styles.infoBox}>Unknown event type.</div>;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>FE Event Registrations</h1>
      </div>
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.stack}>
        {/* Event panel */}
        <section style={styles.panel}>
          {selectedEvent ? (
            <>
              <div style={styles.panelTopBar}>
                <div style={styles.panelEyebrow}>Event</div>
                <button
                  type="button"
                  style={styles.ghostButton}
                  onClick={() => { setEventSearch(''); setShowEventModal(true); }}
                >
                  Change event
                </button>
              </div>
              <div style={styles.eventTitle}>{selectedEvent.title}</div>
              <div style={styles.metaRow}>
                <span style={styles.metaItem}>
                  {selectedEvent.date ? dateToMonDDYYYY(selectedEvent.date, false) : 'TBA'}
                </span>
                <span style={styles.metaDot}>·</span>
                <span style={styles.metaItem}>
                  {convertTo12HourFormat(selectedEvent.start_time) || 'TBA'} – {convertTo12HourFormat(selectedEvent.end_time) || 'TBA'}
                </span>
                <span style={styles.metaDot}>·</span>
                <span style={styles.metaItem}>{selectedEvent.location || 'TBA'}</span>
                <span style={styles.metaDot}>·</span>
                <span style={styles.tag}>{selectedEvent.type || 'Unknown'}</span>
                <span style={styles.metaDot}>·</span>
                <span style={styles.metaItem}>{selectedEvent.hoursavailable || 'N/A'} hrs</span>
              </div>
            </>
          ) : (
            <div style={styles.emptyEventState}>
              <div style={styles.emptyEventText}>No event selected</div>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={() => { setEventSearch(''); setShowEventModal(true); }}
              >
                Find an event
              </button>
            </div>
          )}
        </section>

        {/* Registrations panel */}
        {selectedEvent && (
          <section style={styles.panel}>
            <div style={styles.panelTopBar}>
              <div style={styles.panelEyebrow}>
                Registrations <span style={styles.count}>{registrations.length}</span>
                {loadingRegistrations && <span style={styles.loadingText}> · loading…</span>}
              </div>
              <button
                type="button"
                aria-label="Add registration"
                style={styles.iconButton}
                onClick={() => setShowRegisterModal(true)}
              >
                +
              </button>
            </div>

            {registrations.length === 0 ? (
              <div style={styles.noRegistrations}>No registrations yet.</div>
            ) : (
              <div style={styles.regTable}>
                <div style={styles.regHeader}>
                  <div>Student</div>
                  <div>In Person</div>
                  <div>Transport</div>
                  <div>Attended</div>
                  <div style={styles.alignRight}>Actions</div>
                </div>
                {registrations.map((registration) => {
                  const canRemove = registration.status === 'Registered' && registration.attended === null;
                  return (
                    <div key={registration.id} style={styles.regRow}>
                      <div style={styles.regStudent}>
                        {registration.student_name || 'Unknown Student'}
                      </div>
                      <div style={styles.regCell}>{renderInPersonCell(registration.inpersondesired)}</div>
                      <div style={styles.regCell}>{renderTransportCell(registration.needstransportation)}</div>
                      <div style={styles.regCell}>{renderAttendedCell(registration.attended)}</div>
                      <div style={{ ...styles.regCell, ...styles.alignRight }}>
                        {canRemove ? (
                          <button
                            type="button"
                            style={styles.dangerButton}
                            onClick={() => handleRemoveRegistration(registration.id)}
                            disabled={removingRegistration === registration.id}
                          >
                            {removingRegistration === registration.id ? 'Removing…' : 'Remove'}
                          </button>
                        ) : (
                          <span style={styles.muted}>—</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>

      {/* Event picker modal */}
      {showEventModal && (
        <div style={styles.modalOverlay} onClick={() => setShowEventModal(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Select event</div>
              <button type="button" style={styles.ghostButton} onClick={() => setShowEventModal(false)}>Close</button>
            </div>
            <div style={styles.modalBody}>
              <input
                type="text"
                value={eventSearch}
                onChange={(e) => setEventSearch(e.target.value)}
                placeholder="Search by title, date, location, or type…"
                style={styles.searchInput}
                autoFocus
              />

              <div style={styles.eventList}>
                <div style={styles.eventListHeader}>
                  <div style={styles.colDate}>Date</div>
                  <div style={styles.colTitle}>Title</div>
                  <div style={styles.colType}>Type</div>
                  <div style={styles.colLoc}>Location</div>
                </div>
                <div style={styles.eventListBody}>
                  {filteredEvents.length === 0 ? (
                    <div style={styles.noRegistrations}>No matching events.</div>
                  ) : (
                    filteredEvents.map(ev => (
                      <button
                        key={ev.id}
                        type="button"
                        style={{
                          ...styles.eventRow,
                          ...(ev.id === selectedEventId ? styles.eventRowSelected : {})
                        }}
                        onClick={() => {
                          handleEventChange(ev.id);
                          setShowEventModal(false);
                        }}
                      >
                        <div style={styles.colDate}>{ev.date ? dateToMonDDYYYY(ev.date, false) : '—'}</div>
                        <div style={{ ...styles.colTitle, ...styles.eventRowTitle }}>{ev.title || 'Untitled'}</div>
                        <div style={styles.colType}>
                          <span style={styles.tag}>{ev.type || 'Unknown'}</span>
                        </div>
                        <div style={{ ...styles.colLoc, ...styles.muted }}>{ev.location || '—'}</div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Register student modal */}
      {showRegisterModal && selectedEvent && (
        <div style={styles.modalOverlay} onClick={() => setShowRegisterModal(false)}>
          <div style={{ ...styles.modalCard, ...styles.modalCardSmall }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Register student</div>
              <button type="button" style={styles.ghostButton} onClick={() => setShowRegisterModal(false)}>Close</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Student</label>
                <select
                  value={selectedEnrollmentId}
                  onChange={(e) => handleEnrollmentChange(e.target.value)}
                  style={styles.select}
                >
                  <option value="">Choose a student…</option>
                  {enrollments.map(enrollment => (
                    <option key={enrollment.enrollment_id} value={enrollment.enrollment_id}>
                      {enrollment.account?.last_name}, {enrollment.account?.first_name} – {enrollment.school_name} ({dateTimeToMonDDYYYYWithTime(enrollment.start_date, false)} to {(enrollment.end_date)?dateTimeToMonDDYYYYWithTime(enrollment.end_date, false): 'Ongoing'})
                    </option>
                  ))}
                </select>
              </div>
              {selectedEnrollmentId ? (
                renderRegistrationForm()
              ) : (
                <div style={styles.hint}>Pick a student to continue.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const colors = {
  text: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
  borderStrong: '#cbd5e1',
  surface: '#ffffff',
  surfaceAlt: '#f8fafc',
  surfaceHover: '#f1f5f9',
  danger: '#b91c1c',
  dangerBg: '#fef2f2',
  dangerBorder: '#fecaca',
  successBg: '#f0fdf4',
  successBorder: '#bbf7d0',
  successText: '#166534',
  errorBg: '#fef2f2',
  errorBorder: '#fecaca',
  errorText: '#991b1b'
};

const fontStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif';

const styles = {
  container: {
    maxWidth: '960px',
    margin: '20px auto',
    padding: '16px',
    backgroundColor: colors.surface,
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    fontFamily: fontStack,
    color: colors.text
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px'
  },
  title: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: colors.text,
    margin: 0,
    letterSpacing: '-0.01em'
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  panel: {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
    padding: '12px'
  },
  panelTopBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
    minHeight: '24px'
  },
  panelEyebrow: {
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: colors.textMuted,
    fontWeight: 600
  },
  count: {
    display: 'inline-block',
    minWidth: '18px',
    padding: '0 6px',
    marginLeft: '4px',
    borderRadius: '999px',
    backgroundColor: colors.surfaceHover,
    color: colors.textSecondary,
    fontSize: '0.7rem',
    fontWeight: 600,
    textAlign: 'center',
    letterSpacing: 0
  },
  emptyEventState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    padding: '4px 0'
  },
  emptyEventText: {
    fontSize: '0.85rem',
    color: colors.textSecondary
  },
  eventTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: colors.text,
    marginBottom: '4px'
  },
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.8rem',
    color: colors.textSecondary
  },
  metaItem: { whiteSpace: 'nowrap' },
  metaDot: { color: colors.textMuted },
  tag: {
    display: 'inline-block',
    padding: '1px 6px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: colors.textSecondary,
    backgroundColor: colors.surfaceHover,
    border: `1px solid ${colors.border}`,
    borderRadius: '999px'
  },
  loadingText: {
    fontSize: '0.72rem',
    color: colors.textMuted,
    fontWeight: 500,
    textTransform: 'none',
    letterSpacing: 0
  },
  // Buttons
  primaryButton: {
    padding: '6px 10px',
    backgroundColor: colors.text,
    color: '#fff',
    border: `1px solid ${colors.text}`,
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  ghostButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  iconButton: {
    width: '24px',
    height: '24px',
    padding: 0,
    borderRadius: '6px',
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    color: colors.textSecondary,
    fontSize: '0.95rem',
    fontWeight: 600,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  dangerButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    color: colors.danger,
    border: `1px solid ${colors.dangerBorder}`,
    borderRadius: '6px',
    fontSize: '0.72rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  // Forms
  formBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '4px'
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: colors.textSecondary
  },
  radioRow: {
    display: 'flex',
    gap: '12px',
    paddingTop: '2px'
  },
  radioLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.8rem',
    color: colors.text,
    fontWeight: 400,
    cursor: 'pointer'
  },
  select: {
    padding: '6px 8px',
    fontSize: '0.82rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    backgroundColor: colors.surface,
    color: colors.text,
    cursor: 'pointer',
    outline: 'none',
    fontFamily: fontStack
  },
  searchInput: {
    width: '100%',
    padding: '7px 10px',
    fontSize: '0.82rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    backgroundColor: colors.surface,
    color: colors.text,
    outline: 'none',
    fontFamily: fontStack,
    boxSizing: 'border-box',
    marginBottom: '8px'
  },
  // Alerts
  infoBox: {
    background: colors.surfaceAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    padding: '8px 10px',
    color: colors.textSecondary,
    fontSize: '0.8rem'
  },
  hint: {
    fontSize: '0.78rem',
    color: colors.textMuted,
    padding: '6px 0'
  },
  error: {
    padding: '7px 10px',
    backgroundColor: colors.errorBg,
    color: colors.errorText,
    borderRadius: '6px',
    marginBottom: '8px',
    border: `1px solid ${colors.errorBorder}`,
    fontSize: '0.8rem'
  },
  success: {
    padding: '7px 10px',
    backgroundColor: colors.successBg,
    color: colors.successText,
    borderRadius: '6px',
    marginBottom: '8px',
    border: `1px solid ${colors.successBorder}`,
    fontSize: '0.8rem'
  },
  // Registrations table
  noRegistrations: {
    padding: '14px',
    color: colors.textMuted,
    textAlign: 'center',
    background: colors.surfaceAlt,
    border: `1px dashed ${colors.border}`,
    borderRadius: '8px',
    fontSize: '0.8rem'
  },
  regTable: {
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: colors.surface
  },
  regHeader: {
    display: 'grid',
    gridTemplateColumns: 'minmax(160px, 2fr) 90px 100px 90px 100px',
    gap: '10px',
    padding: '7px 10px',
    backgroundColor: colors.surfaceAlt,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: colors.textMuted
  },
  regRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(160px, 2fr) 90px 100px 90px 100px',
    gap: '10px',
    padding: '8px 10px',
    borderTop: `1px solid ${colors.border}`,
    fontSize: '0.82rem',
    alignItems: 'center'
  },
  regStudent: {
    fontWeight: 500,
    color: colors.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  regCell: {
    color: colors.textSecondary
  },
  alignRight: { textAlign: 'right', justifyContent: 'flex-end' },
  muted: { color: colors.textMuted },
  inlineIconCell: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    color: colors.textSecondary,
    lineHeight: 1
  },
  tagAttended: {
    display: 'inline-block',
    padding: '1px 7px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#166534',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '999px'
  },
  tagAbsent: {
    display: 'inline-block',
    padding: '1px 7px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#b91c1c',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '999px'
  },
  // Modal
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.32)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '64px 16px 16px'
  },
  modalCard: {
    width: 'min(720px, 100%)',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.surface,
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
    overflow: 'hidden',
    fontFamily: fontStack
  },
  modalCardSmall: {
    width: 'min(440px, 100%)'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.surface
  },
  modalTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: colors.text
  },
  modalBody: {
    padding: '10px 12px',
    overflowY: 'auto'
  },
  // Event picker rows
  eventList: {
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: colors.surface
  },
  eventListHeader: {
    display: 'grid',
    gridTemplateColumns: '110px 1fr 90px 1fr',
    gap: '10px',
    padding: '7px 10px',
    backgroundColor: colors.surfaceAlt,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: colors.textMuted
  },
  eventListBody: {
    maxHeight: '52vh',
    overflowY: 'auto'
  },
  eventRow: {
    display: 'grid',
    gridTemplateColumns: '110px 1fr 90px 1fr',
    gap: '10px',
    width: '100%',
    padding: '8px 10px',
    border: 'none',
    borderTop: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: fontStack,
    fontSize: '0.82rem',
    color: colors.textSecondary,
    alignItems: 'center'
  },
  eventRowSelected: {
    backgroundColor: colors.surfaceHover
  },
  eventRowTitle: {
    color: colors.text,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  colDate: { whiteSpace: 'nowrap' },
  colTitle: { minWidth: 0 },
  colType: { whiteSpace: 'nowrap' },
  colLoc: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
};

export default FEeventRegistrationsManager; 